import {
	ArrowPathRoundedSquareIcon,
	PlusIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getEvents } from '../../api/events';

import { Calendar } from '../../components/Calendar';
import EventItem from '../../components/Events_to_deprecate/EventItem';
import {
	classNames,
	getReduced,
	getUserFullName,
} from '../../components/helpers/functions';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import Badge from '../../components/Badge';
import Modal from '../../components/Shell/Modal';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useSearchParams from '../../hooks/useSearchParams';
import usePrevious from '../../hooks/usePrevious';
import useSettings from '../../hooks/useSettings';

const Overview = () => {
	const { startDate, endDate } = useSearchParams();
	const { timezone } = useSettings();

	const navigate = useNavigate();
	const [monthSteps, setMonthSteps] = useState(0);
	const [calendarModalOpen, setCalendarModalOpen] = useState(false);

	const { open: eventDetailsOptions, setOpen: openEventModal } =
		useModal('eventDetails');
	const { open: isAddEventModalOpen } = useModal('eventAdd');
	const { open, setOpen: openEventAddModal } = useModal('eventAdd');

	const [upcomingMaxDays, setUpcomingMaxDays] = useState(7);

	const { value: events, execute: refreshEvents } = useAsync(getEvents, {
		defaultPayload: {
			startDate: moment().tz(timezone).format('MM/DD/YYYY'),
			status: ['SCHEDULED'],
			...(upcomingMaxDays
				? {
						endDate: moment()
							.tz(timezone)
							.add(upcomingMaxDays, 'd')
							.format('MM/DD/YYYY'),
				  }
				: {}),
		},
	});

	const { value: selectedEvents, execute: getEventsForDateRange } = useAsync(
		getEvents,
		{ immediate: false }
	);

	const { value: pastOverdueEvents } = useAsync(getEvents, {
		defaultPayload: {
			endDate: moment()
				.tz(timezone)
				.subtract(1, 'd')
				.endOf('d')
				.format('MM/DD/YYYY'),
			status: ['ESTIMATED', 'SCHEDULED'],
		},
	});

	const { value: upcomingUnscheduledEvents } = useAsync(getEvents, {
		defaultPayload: {
			startDate: moment().tz(timezone).startOf('d').format('MM/DD/YYYY'),
			endDate: moment()
				.tz(timezone)
				.add(30, 'd')
				.endOf('d')
				.format('MM/DD/YYYY'),
			status: ['ESTIMATED'],
		},
	});

	useEffect(() => {
		if (startDate || endDate) {
			setCalendarModalOpen(true);
		}
	}, [startDate, endDate]);

	const prevCalendarModalOpen = usePrevious(calendarModalOpen);

	useEffect(() => {
		if (!calendarModalOpen && prevCalendarModalOpen) {
			navigate({ search: '' }, { replace: true });
		}
	}, [calendarModalOpen]);

	const prevEventDetailsModal = usePrevious(eventDetailsOptions);
	const prevAddEventModal = usePrevious(isAddEventModalOpen);

	const { value: calendarEvents, execute: refreshCalendarEvents } = useAsync(
		getEvents,
		{
			defaultPayload: {
				startDate: moment()
					.tz(timezone)
					.add(monthSteps, 'months')
					.startOf('month')
					.startOf('day')
					.format('MM/DD/YYYY'),
				endDate: moment()
					.tz(timezone)
					.add(monthSteps + 1, 'months')
					.endOf('month')
					.endOf('day')
					.format('MM/DD/YYYY'),
				status: ['SCHEDULED'],
			},
		}
	);

	useEffect(() => {
		if (
			(!eventDetailsOptions && prevEventDetailsModal) ||
			(!isAddEventModalOpen && prevAddEventModal)
		) {
			refreshEvents();
			if (startDate || endDate) {
				setCalendarModalOpen(true);
				getEventsForDateRange({
					startDate: moment(startDate)
						.tz(timezone)
						.startOf('day')
						.format('MM/DD/YYYY'),
					endDate: moment(endDate)
						.tz(timezone)
						.startOf('day')
						.format('MM/DD/YYYY'),
					status: ['SCHEDULED'],
				});
			} else {
				setCalendarModalOpen(false);
			}

			refreshCalendarEvents(
				{
					startDate: moment()
						.tz(timezone)
						.add(monthSteps, 'months')
						.startOf('month')
						.startOf('day')
						.format('MM/DD/YYYY'),
					endDate: moment()
						.tz(timezone)
						.add(monthSteps + 1, 'months')
						.endOf('month')
						.endOf('day')
						.format('MM/DD/YYYY'),
				},
				null,
				{ silentExecute: true }
			);
		}
	}, [eventDetailsOptions, isAddEventModalOpen]);

	useEffect(() => {
		refreshCalendarEvents(
			{
				startDate: moment()
					.tz(timezone)
					.add(monthSteps, 'months')
					.startOf('month')
					.startOf('day')
					.format('MM/DD/YYYY'),
				endDate: moment()
					.tz(timezone)
					.add(monthSteps + 1, 'months')
					.endOf('month')
					.endOf('day')
					.format('MM/DD/YYYY'),
			},
			null,
			{ silentExecute: true }
		);
	}, [monthSteps]);

	useEffect(() => {
		refreshEvents(
			{
				startDate: moment().tz(timezone).startOf('day').format('MM/DD/YYYY'),
				...(upcomingMaxDays
					? {
							endDate: moment()
								.tz(timezone)
								.add(upcomingMaxDays, 'd')
								.endOf('d')
								.format('MM/DD/YYYY'),
					  }
					: {}),
			},
			null,
			{ silent: true, overrideDefault: true }
		);
	}, [upcomingMaxDays]);

	useEffect(() => {
		if (!isAddEventModalOpen) {
			refreshEvents();
		}
	}, [isAddEventModalOpen]);

	useEffect(() => {
		if (startDate || endDate) {
			getEventsForDateRange({
				startDate,
				endDate,
			});
		}
	}, [startDate, endDate]);

	const groupedByStatuses = getReduced(events?.rows || [], 'status');

	const attentionEvents = [
		...(pastOverdueEvents?.rows?.map((event) => ({
			...event,
			type: 'overdue',
		})) || []),
		...(upcomingUnscheduledEvents?.rows?.map((event) => ({
			...event,
			type: 'unscheduled',
		})) || []),
	];

	return (
		<MainContent maxWidth="w-full">
			<div className="grid grid-cols-12 gap-x-12">
				<div className="col-span-10 col-start-2 pb-10">
					<PageHeading
						gap="md"
						divider={false}
						actions={[
							{
								name: (
									<>
										<PlusIcon className="w-4 h-4 mr-3" /> Add to Calendar
									</>
								),
								onClick: () => openEventAddModal(true),
							},
						]}
					>
						Calendar
					</PageHeading>

					<div className="mb-12">
						<Calendar
							showMonthAhead
							numberColumns={2}
							monthSteps={monthSteps}
							events={calendarEvents?.rows}
							onPrev={() => {
								setMonthSteps(monthSteps - 1);
							}}
							onNext={() => {
								setMonthSteps(monthSteps + 1);
							}}
							onClick={(date) => {
								setCalendarModalOpen(true);
								navigate(
									`/calendar?startDate=${moment(date)
										.utc()
										.format('MM/DD/YYYY')}&endDate=${moment(date)
										.utc()
										.format('MM/DD/YYYY')}`
								);
							}}
						/>
					</div>

					<div>
						<div className="flex flex-col items-center">
							<div className="w-3/5">
								<h4 className="text-lg text-gray-800 font-semibold flex justify-between items-center mb-4">
									<div>
										Upcoming{' '}
										<span className="text-xs text-gray-500 font-normal">
											{upcomingMaxDays
												? ` - Next ${upcomingMaxDays} days`
												: ' - All events'}
										</span>
									</div>
									<div className="flex text-xs text-gray-400 cursor-pointer">
										<div
											className={classNames(
												'ml-2',
												upcomingMaxDays === 7 && 'text-gray-800'
											)}
											onClick={() => setUpcomingMaxDays(7)}
										>
											7D
										</div>
										<div
											className={classNames(
												'ml-2',
												upcomingMaxDays === 30 && 'text-gray-800'
											)}
											onClick={() => setUpcomingMaxDays(30)}
										>
											1M
										</div>
										<div
											className={classNames(
												'ml-2',
												upcomingMaxDays === 90 && 'text-gray-800'
											)}
											onClick={() => setUpcomingMaxDays(90)}
										>
											3M
										</div>
										<div
											className={classNames(
												'ml-2',
												!upcomingMaxDays && 'text-gray-800'
											)}
											onClick={() => setUpcomingMaxDays(null)}
										>
											ALL
										</div>
									</div>
								</h4>

								{groupedByStatuses?.SCHEDULED?.length ? (
									groupedByStatuses.SCHEDULED.sort(
										(a, b) =>
											new Date(a.scheduledDate) - new Date(b.scheduledDate)
									).map((event, i) => {
										const title = event?.service?.engineer
											? `${getUserFullName(event?.service?.engineer)}${
													event?.service?.engineer?.company
														? ` from ${event?.service?.engineer?.company?.companyName}`
														: ''
											  }`
											: event?.title || event?.description;

										const assets = [
											...(event?.assets?.length ? event?.assets : []),
										];
										const date = moment(event?.scheduledDate);
										let tags = [];

										if (event?.type === 'asset.service') {
											tags.push(
												<Badge status={'info'} isBeta className="mt-2 text-xxs">
													<ArrowPathRoundedSquareIcon className="w-4 h-4 mr-2" />

													{`${event?.service?.name}`}
												</Badge>
											);
										}

										return (
											<EventItem
												assets={assets}
												date={date}
												endDate={event?.scheduledDateEnd}
												title={title}
												tags={tags}
												onClick={() => openEventModal({ id: event.id })}
												timezone={timezone}
												allDay={!!event?.allDayFlags > 0}
											/>
										);
									})
								) : (
									<p className="text-center text-gray-500 py-24 text-xs">
										{upcomingMaxDays
											? `No events scheduled in the next ${upcomingMaxDays} days`
											: 'No events scheduled'}
									</p>
								)}
							</div>
						</div>
					</div>
				</div>

				<Modal
					title={
						<div>
							Selected Date Range{' '}
							<span className="text-xs text-gray-500 font-normal">
								{`${startDate} to ${endDate}`}
							</span>
						</div>
					}
					open={!!calendarModalOpen}
					onClose={() => setCalendarModalOpen(false)}
				>
					{startDate && endDate ? (
						<div className="mb-6">
							{selectedEvents?.rows?.length ? (
								selectedEvents?.rows
									?.sort(
										(a, b) =>
											new Date(a.scheduledDate) - new Date(b.scheduledDate)
									)
									.map((event, i) => {
										const title = event?.service?.engineer
											? `${getUserFullName(event?.service?.engineer)}${
													event?.service?.engineer?.company
														? ` from ${event?.service?.engineer?.company?.companyName}`
														: ''
											  }`
											: event?.title || event?.description;

										const assets = [
											...(event?.assets?.length ? event?.assets : []),
										];
										const date = moment(event?.scheduledDate).tz(timezone);
										let tags = [];

										if (event?.type === 'asset.service') {
											tags.push(
												<Badge status={'info'} isBeta className="mt-2 text-xxs">
													<ArrowPathRoundedSquareIcon className="w-4 h-4 mr-2" />

													{`${event?.service?.name}`}
												</Badge>
											);
										}

										return (
											<EventItem
												assets={assets}
												date={date}
												endDate={event?.scheduledDateEnd}
												title={title}
												tags={tags}
												onClick={() => openEventModal({ id: event.id })}
												timezone={timezone}
												allDay={!!event?.allDayFlags > 0}
											/>
										);
									})
							) : (
								<p className="text-center text-gray-500 py-6 text-xs">
									No events found for this date range
								</p>
							)}
						</div>
					) : null}
				</Modal>
			</div>
		</MainContent>
	);
};

export default Overview;
